<script lang="ts" setup>
import type HtmlTag from '~/types/htmlTag'

type Props = {
  forceFullHeight?: boolean
  hasSpotifyTheme?: boolean
  htmlTag?: HtmlTag
  small?: boolean
  spotifyPlaylistId: string
  followers?: number | null
}
type Emits = {
  'iframe-click': []
}

const emit = defineEmits<Emits>()
const props = withDefaults(defineProps<Props>(), {
  htmlTag: 'div',
  forceFullHeight: false,
  small: false,
  hasSpotifyTheme: false,
  followers: null,
})

const loading = ref(false)
const clickEventLatch = ref(false)

onMounted(() => {
  window.focus()
  window.addEventListener('blur', handleNativeIframeClick)
})
onBeforeUnmount(() =>
  window.removeEventListener('blur', handleNativeIframeClick),
)

function handleLoad() {
  loading.value = false
}

function handleNativeIframeClick(): void {
  setTimeout(() => {
    if (clickEventLatch.value) return

    if (document.activeElement?.id === props.spotifyPlaylistId) {
      clickEventLatch.value = true
      emit('iframe-click')
    }
  })
}
</script>

<template>
  <component
    :is="htmlTag"
    class="tw-relative"
    :class="{ 'tw-h-full': forceFullHeight }"
  >
    <div
      class="tw-relative tw-w-full"
      :class="{ 'tw-h-full': forceFullHeight }"
    >
      <ClientOnly>
        <template #fallback>
          <div class="tw-h-20"></div>
        </template>
        <div
          v-if="loading"
          key="loader"
          class="tw-loading-gradient tw-absolute tw-left-0 tw-top-0 tw-h-full tw-w-full"
        />
        <iframe
          :id="spotifyPlaylistId"
          ref="iframe"
          :src="`https://open.spotify.com/embed/playlist/${spotifyPlaylistId}/${
            hasSpotifyTheme ? '' : '?theme=0'
          }`"
          width="100%"
          :height="small ? '80px' : '100%'"
          frameBorder="0"
          allowfullscreen="true"
          allow="autoplay; clipboard-write; encrypted-media; fullscreen;
          picture-in-picture"
          class="tw-overflow-hidden"
          credentialless
          @load="handleLoad"
          @click.once="handleNativeIframeClick"
        />
      </ClientOnly>
    </div>
    <div
      v-if="followers"
      class="tw-mt-2 tw-flex tw-h-8 tw-w-full tw-items-center tw-justify-center tw-gap-1 tw-rounded-xl tw-bg-gradient-to-r tw-from-[#282828] tw-to-[#282828ee] tw-p-2 lg:tw-h-10 lg:tw-p-4"
    >
      <UIIcon
        name="fa6-solid:heart"
        class="tw-text-icon-inverse"
        size="sm"
        aria-hidden
      />
      <span class="tw-text-sm tw-font-medium tw-text-inverse">{{
        $t(
          'components.influencer.profile.SpotifyPlaylistDisplayCollection.cta.likes',
          { count: formatNumber(followers) },
        )
      }}</span>
    </div>
  </component>
</template>
